<!-- ============================================================
  EditHistoryTimeline — ไทม์ไลน์ "ใครแก้อะไรเมื่อไหร่" จาก taction_log (backend /getEditHistory)
  ใช้ร่วมกัน 4 จอ: จองคลาส / จอง Private / คอร์สลูกค้า / ประวัติคอร์ส — ห้าม copy ไปแปะรายจอ

  กติกาที่ตั้งใจ:
  - fetch ตอน prop refid เปลี่ยน (เปิด dialog) เท่านั้น ไม่โพล · ล้มเงียบเป็นข้อความสั้น
    dialog แม่ต้องเปิดได้เสมอแม้ประวัติโหลดไม่ได้
  - changed_json เก็บชื่อคอลัมน์ DB ดิบ — FIELD_LABEL_KEYS แปลเป็นคีย์ i18n (5 ภาษา) ที่นี่
    ที่เดียว · ฟิลด์ระบบที่ผู้ใช้ไม่ควรเห็น (updateby, classid, courseid) ถูกซ่อน — classid
    ซ่อนเพราะ classtime/classdate ที่มากับ diff เดียวกันสื่อความหมายเดียวกันในภาษาคนอยู่แล้ว
  - ฟิลด์ที่ไม่รู้จัก (action ใหม่ในอนาคต) แสดงชื่อดิบ ไม่ซ่อนเงียบ — ข้อมูล audit ห้ามหายไปเฉย ๆ
  - ทิ้งผลที่มาช้า (เทียบ refid) — กันประวัติใบเก่าโผล่บนใบใหม่ idiom เดียวกับรูปเด็ก
============================================================ -->
<template>
  <div>
    <div class="modal-sec mt"><span class="mdi mdi-history"></span> {{ $t('editHistory.title') }}</div>
    <!-- กันที่ไว้เงียบ ๆ ไม่ใช้ .id-skel — บล็อกนี้อยู่ใน dialog ที่เพิ่งเปิด shimmer จึงกลายเป็น
         แถบวาบทุกครั้ง · ข้อความขึ้นเฉพาะตอนช้าเกิน SLOW_MS -->
    <div v-if="loading" class="eht-loading">
      <span v-if="loadingSlow">{{ $t('common.loading') }}</span>
    </div>
    <template v-else>
      <div v-if="!items.length" class="t-cap" style="padding:2px 0 4px">{{ $t('editHistory.empty') }}</div>
      <div v-else class="eht-list">
        <div v-for="(e, i) in items" :key="i" class="eht-entry">
          <div class="eht-head">{{ $formatDateTime(e.at) }} · {{ e.actor }}</div>
          <div v-if="e.summary" class="eht-sum">{{ e.summary }}</div>
          <table v-if="e.fields.length" class="eht-tab">
            <tr v-for="f in e.fields" :key="f.key">
              <td class="eht-f">{{ f.label }}</td>
              <td class="eht-old">{{ f.oldText }} <span class="mdi mdi-arrow-right eht-arr"></span></td>
              <td class="eht-new">{{ f.newText }}</td>
            </tr>
          </table>
        </div>
      </div>
      <div class="t-cap" style="margin-top:6px"><span class="mdi mdi-information-outline"></span> {{ $t('editHistory.since') }}</div>
    </template>
  </div>
</template>

<script>
import axios from 'axios';

// ชื่อคอลัมน์ DB → คีย์ i18n · ใช้คีย์เดิมของระบบให้มากที่สุด (เพิ่มใหม่เฉพาะที่ไม่เคยมี)
const FIELD_LABEL_KEYS = {
  classdate: 'table.date',
  bookingdate: 'table.date',
  classtime: 'table.time',
  price: 'table.price',
  remaining: 'table.remaining',
  expiredate: 'table.expireDate',
  startdate: 'table.startDate',
  paydate: 'customerCourse.payDate',
  paymentmethod: 'customerCourse.paymentMethod',
  payment_fee: 'customerCourse.paymentFee',
  period: 'customerCourse.period',
  paid: 'editHistory.fieldPaid',
  checkedin: 'table.checkin',
  finish: 'editHistory.fieldFinish',
  delflag: 'editHistory.fieldCancelled',
  coursetype: 'table.courseType',
  coursename: 'table.courseName',
  courserefer: 'table.courseRef',
  studentid: 'table.studentId',
  note: 'editHistory.fieldNote',
  shortnote: 'editHistory.fieldNote',
  blindflag: 'bookingMgmt.visibility',
  coachusername: 'privateBooking.coach',
  privatetype: 'privateBooking.type',
  hours: 'privateBooking.hours',
  students: 'privateBooking.students',
  is_promotion: 'editHistory.fieldPromotion',
  enable_double_booking: 'editHistory.fieldDoubleBooking',
  slip_image_url: 'editHistory.fieldSlip',
  // ฟิลด์นักเรียน (tstudent) — ประวัติแก้ไข/ย้ายครอบครัว/ลบ
  firstname: 'editHistory.fieldFirstname',
  middlename: 'editHistory.fieldMiddlename',
  lastname: 'editHistory.fieldLastname',
  nickname: 'editHistory.fieldNickname',
  gender: 'editHistory.fieldGender',
  dateofbirth: 'editHistory.fieldDob',
  level: 'table.level',
  school: 'editHistory.fieldSchool',
  familyid: 'editHistory.fieldFamily',
  courserefer2: 'editHistory.fieldCourseRef2',
  // ฟิลด์บัญชีผู้ใช้ (tuser)
  email: 'editHistory.fieldEmail',
  mobileno: 'table.phone',
  address: 'editHistory.fieldAddress',
  usertype: 'editHistory.fieldRole',
  access_restricted: 'editHistory.fieldAccess',
};
// usertype: เก็บเป็น '0'/'1'/'2'/'10' — แปลงเป็นชื่อสิทธิ์ให้อ่านออก
const ROLE_LABELS = { 0: 'editHistory.roleHead', 1: 'editHistory.roleAdmin', 2: 'editHistory.roleCoach', 10: 'editHistory.roleCustomer' };
// ฟิลด์ระบบ — ผู้ใช้ไม่รู้จัก id ภายใน และ updateby ซ้ำกับหัวรายการอยู่แล้ว
const HIDDEN_FIELDS = new Set(['updateby', 'classid', 'courseid']);
const DATE_FIELDS = new Set(['classdate', 'bookingdate', 'paydate', 'expiredate', 'startdate', 'dateofbirth']);
const MONEY_FIELDS = new Set(['price', 'payment_fee', 'gym_fee', 'organizer_fee']);
// ฟิลด์ 0/1 — คู่คำแปลต่างกันตามความหมาย ไม่ใช่ boolean กลาง ๆ
const BOOL_FIELDS = {
  paid: ['customerCourse.unpaid', 'customerCourse.paid'],
  checkedin: ['editHistory.valNotChecked', 'editHistory.valChecked'],
  finish: ['editHistory.valActive', 'editHistory.valFinished'],
  delflag: ['editHistory.valActive', 'editHistory.valCancelled'],
  blindflag: ['bookingMgmt.visPublic', 'bookingMgmt.visPrivate'],
  is_promotion: ['editHistory.valNo', 'editHistory.valYes'],
  enable_double_booking: ['editHistory.valNo', 'editHistory.valYes'],
  access_restricted: ['editHistory.valActive', 'editHistory.valRestricted'],
};
const PAYMENT_LABELS = { cash: 'customerCourse.paymentCash', transfer: 'customerCourse.paymentTransfer', credit: 'customerCourse.paymentCredit' };

// ดึงประวัติเสร็จเร็วกว่านี้เกือบทุกครั้ง — โชว์ตัวบอกสถานะทันทีจะกลายเป็นแถบวาบตอนเปิด dialog
const SLOW_MS = 400;

export default {
  name: 'EditHistoryTimeline',
  props: {
    // ประวัติที่พ่อแม่ดึงมาให้ล่วงหน้าก่อนเปิด dialog (fetchEditHistory ใน utils/history.js)
    // มีค่า = ไม่ต้องยิงเอง ผู้ใช้จึงเห็นไทม์ไลน์ครบตั้งแต่วินาทีที่ dialog โผล่
    preloaded: { type: Array, default: null },
    kind: { type: String, required: true },     // booking | private | customercourse
    refid: { type: [String, Number], default: null },
  },
  data() {
    // baseURL มาจาก app.config.globalProperties (main.js) เหมือนทุก component ในระบบ
    return { loading: false, loadingSlow: false, items: [] };
  },
  watch: {
    refid: {
      immediate: true,
      handler() {
        if (this.preloaded) { this.items = this.preloaded.map((e) => this.shape(e)); return; }
        this.fetch();
      },
    },
  },
  beforeUnmount() {
    clearTimeout(this.slowTimer);
  },
  methods: {
    async fetch() {
      const refid = this.refid;
      this.items = [];
      this.loading = false;
      this.loadingSlow = false;
      clearTimeout(this.slowTimer);
      if (refid === null || refid === undefined || refid === '') return;
      this.loading = true;
      this.slowTimer = setTimeout(() => { this.loadingSlow = true; }, SLOW_MS);
      try {
        const r = await axios.post(this.baseURL + '/getEditHistory', { kind: this.kind, refid },
          { headers: { Authorization: `Bearer ${this.$store.getters.getToken}` } });
        if (String(this.refid) !== String(refid)) return; // ผู้ใช้เปิดใบอื่นไปแล้ว — ทิ้งผลนี้
        if (r.data.success) this.items = (r.data.history || []).map((e) => this.shape(e));
      } catch (err) { /* ล้มเงียบ — โชว์ "ไม่มีประวัติ" ดีกว่าทำให้ dialog แม่เปิดไม่ได้ */
      } finally {
        // ปิด loading เฉพาะเมื่อคำขอนี้ยังเป็นของ refid ปัจจุบัน — คำขอเก่าที่ค้างอยู่ห้ามแตะ
        // สถานะของคำขอใหม่ (ผลรีวิว: เดิม early-return ข้ามการปิด loading แล้ว skeleton ค้างถาวร)
        if (String(this.refid) === String(refid)) {
          clearTimeout(this.slowTimer);
          this.loading = false;
          this.loadingSlow = false;
        }
      }
    },
    shape(entry) {
      const changed = entry.changed_json || {};
      const fields = Object.keys(changed)
        .filter((k) => !HIDDEN_FIELDS.has(k))
        .map((k) => ({
          key: k,
          label: FIELD_LABEL_KEYS[k] ? this.$t(FIELD_LABEL_KEYS[k]) : k,
          oldText: this.fmt(k, changed[k].old),
          newText: this.fmt(k, changed[k].new),
        }));
      // การย้ายใบจอง backend เขียน title สรุปไว้แล้ว (เช่น "ย้ายการจอง … → …") ใช้เป็นบรรทัดนำ
      // เฉพาะเคสนั้น — title ของ action อื่นเป็นแนว log ("แพ็กเกจ X — remaining ที่ส่งมา: …") ไม่ช่วยผู้ใช้
      const summary = entry.action_key === 'booking.update' && entry.title ? entry.title : '';
      return { at: entry.at, actor: entry.actor, summary, fields };
    },
    fmt(key, v) {
      if (v === null || v === undefined || v === '') return '—';
      if (key in BOOL_FIELDS) return this.$t(BOOL_FIELDS[key][Number(v) ? 1 : 0]);
      if (key === 'usertype') return ROLE_LABELS[v] ? this.$t(ROLE_LABELS[v]) : String(v);
      if (key === 'paymentmethod') return PAYMENT_LABELS[v] ? this.$t(PAYMENT_LABELS[v]) : String(v);
      // สลิปเป็น URL ถาวรยาว ๆ — ผู้ใช้สนใจแค่ "มี/เปลี่ยน/เอาออก" โชว์ชื่อไฟล์ท้าย path พอ
      if (key === 'slip_image_url') return String(v).split('/').pop().split('?')[0];
      if (DATE_FIELDS.has(key)) return this.$formatShortDate(v);
      if (MONEY_FIELDS.has(key)) return this.$money(v);
      return String(v);
    },
  },
};
</script>

<style scoped>
/* ระหว่างโหลด: กันที่ไว้เงียบ ๆ (ห้าม .id-skel — shimmer วาบทุกครั้งที่ dialog เปิด) */
.eht-loading { min-height: 34px; display: flex; align-items: center; font-size: 13px; color: var(--c-text-muted); }
.eht-list { display: flex; flex-direction: column; gap: 10px; }
.eht-entry { border-left: 2px solid var(--c-primary-light); padding-left: 10px; }
.eht-head { font-size: 12px; color: var(--c-text-muted); }
.eht-sum { font-size: 13px; color: var(--c-text-body); margin-top: 2px; }
.eht-tab { width: 100%; border-collapse: collapse; font-size: 13px; margin-top: 2px; }
.eht-tab td { padding: 2px 0; vertical-align: top; }
.eht-f { color: var(--c-text-muted); width: 110px; padding-right: 8px; }
.eht-old { color: var(--c-error); opacity: .75; }
.eht-arr { font-size: 12px; color: var(--c-text-muted); opacity: 1; }
.eht-new { color: var(--c-success); font-weight: 600; text-align: right; }
</style>
